import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LabeledValue, SelectValue } from 'antd/lib/select';
import { useRecoilValue } from 'recoil';

import { AgentToolsetSettings, ToolsetModel } from '../../../../../../gamma-api';
import IbDebounceSelect from '../../../../../components/common/IbDebounceSelect';
import { defaultApi } from '../../../../../apis';
import { currentFolderIdSelector } from '../../../../../recoil';
import PromptEditorExpandable from '../../../../../components/common/PromptEditorExpandable';
import { PromptLanguage } from '../../../../../components/common/PromptEditor/const';
import { CodeEditorSize } from '../../../../../components/common/CodeEditorExpandable/types';
import IbTypography from '../../../../../components/common/IbTypography';

import { FORM_ITEM_CLASS_NAME, PARAMETERS_CLASS_NAME } from './const';

const isValid = (settings: AgentToolsetSettings) => !!settings.agent_id;

export interface IAgentParametersProps {
  editToolset: ToolsetModel;
  allowValidationErrorDisplay: boolean;
  onChange: (toolset: ToolsetModel) => void;
  onValidationStateChange: (state: boolean) => void;
}

const AgentParameters: React.FC<IAgentParametersProps> = ({
  editToolset,
  allowValidationErrorDisplay,
  onChange,
  onValidationStateChange,
}) => {
  const { t } = useTranslation();
  const folderId = useRecoilValue(currentFolderIdSelector);

  const [selectedAgent, setSelectedAgent] = useState<SelectValue>();

  const agentSettings = editToolset.settings as AgentToolsetSettings;
  const showAgentError = allowValidationErrorDisplay && !isValid(agentSettings);

  const fetchAgents = async (searchText: string): Promise<SelectValue[]> => {
    try {
      const response = await defaultApi.searchAgents(searchText, [folderId], undefined, [editToolset.agent_id]);
      return (response.data.items || []).map((agent) => ({ value: agent.agent_id, label: agent.name }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      return [];
    }
  };

  const tryUpdateSelectedAgent = async () => {
    if (!agentSettings.agent_id || selectedAgent) {
      return;
    }

    try {
      const response = await defaultApi.getOneAgent(agentSettings.agent_id);
      setSelectedAgent({ value: response.data.agent_id, label: response.data.name });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onAgentChange = (value: SelectValue) => {
    const updatedAgentSettings = {
      ...agentSettings,
      agent_id: (value as LabeledValue).value as string,
    };

    setSelectedAgent(value);
    onChange({ ...editToolset, settings: updatedAgentSettings } as ToolsetModel);
    onValidationStateChange(isValid(updatedAgentSettings));
  };

  const onCallAgentToolDescriptionChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        call_agent_tool_settings: {
          ...agentSettings.call_agent_tool_settings,
          tool_description: value,
        },
      },
    } as ToolsetModel);
  };

  const onAgentIdChange = () => {
    tryUpdateSelectedAgent().finally();
  };
  useEffect(onAgentIdChange, [agentSettings.agent_id]);

  useEffect(() => onValidationStateChange(isValid(agentSettings)), []);

  return (
    <div className={PARAMETERS_CLASS_NAME}>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Toolset: Modal: Settings: Agent: Title')}
        </IbTypography.Paragraph>
        <IbDebounceSelect
          showSearch
          fetchOptions={fetchAgents}
          status={showAgentError ? 'error' : 'default'}
          value={selectedAgent}
          onChange={onAgentChange}
        />
        {showAgentError && (
          <IbTypography.Paragraph error type="descriptor">
            {t('Agent: Toolset: Modal: Settings: Agent: Validation: Required')}
          </IbTypography.Paragraph>
        )}
      </div>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Toolset: Modal: Settings: Call agent tool description: Title')}
        </IbTypography.Paragraph>
        <PromptEditorExpandable
          promptLanguage={PromptLanguage.PLAIN_TEXT}
          size={CodeEditorSize.XXL}
          value={agentSettings.call_agent_tool_settings.tool_description}
          onChange={onCallAgentToolDescriptionChange}
        />
      </div>
    </div>
  );
};

export default AgentParameters;
